import React, { useState, useEffect, useCallback } from 'react';
import { Button, Modal, Toast } from 'react-bootstrap';
import { useAuth } from '../context/AuthContext';
import { fetchWithCSRF } from '../utils/csrfUtils';
import { getTodayLocalDateString } from '../utils/dateUtils';
import { getIngredientImageUrl } from '../utils/imageUtils';
import { useUpdatePantryIngredient, useAddPantryIngredient, useConfirmHasEnough, useConfirmHasLessThanEnough } from '../hooks/pantryIngredient';
import ErrorBoundary from '../components/ErrorBoundary';
import GroceryDrawer from '../components/GroceryDrawer';
import RecipesForIngredientDrawer from '../components/RecipesForIngredientDrawer';
import '../index.css';

const PantryAndGroceries = () => {
    const { pgUser, sessionToken } = useAuth();
    const { updatePantryIngredient } = useUpdatePantryIngredient();
    const { addPantryIngredient } = useAddPantryIngredient();
    const { confirmHasEnough } = useConfirmHasEnough();
    const { confirmHasLessThanEnough } = useConfirmHasLessThanEnough();
    
    // State declarations
    const [isAuthLoading, setIsAuthLoading] = useState(true);
    const [pantryId, setPantryId] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [currentGroceryList, setCurrentGroceryList] = useState(null);
    const [pantryIngredients, setPantryIngredients] = useState([]);
    const [combinedData, setCombinedData] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [editingQuantityId, setEditingQuantityId] = useState(null);
    const [editingQuantity, setEditingQuantity] = useState('');
    const [showToast, setShowToast] = useState(false);
    const [toastMessage, setToastMessage] = useState('');
    const [toastType, setToastType] = useState('success'); // 'success' or 'error'
    const [initialCategories, setInitialCategories] = useState(null); // Store initial categorization
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [isRecipeDrawerOpen, setIsRecipeDrawerOpen] = useState(false);
    const [selectedIngredientForRecipes, setSelectedIngredientForRecipes] = useState(null);
    const [checkedIngredients, setCheckedIngredients] = useState(new Set()); // Track which ingredients user has checked
    const [buttonStates, setButtonStates] = useState(new Map()); // Track button states by ingredient_id: 'enough' | 'none'
    const [groceryDrawerRefreshKey, setGroceryDrawerRefreshKey] = useState(0); // Force drawer to refresh

    // Initialize pgUser-dependent state
    useEffect(() => {
        if (!pgUser) {
            setIsAuthLoading(false);
            return;
        }
        setPantryId(pgUser?.pantry_id || null);
        setIsAuthLoading(false);
    }, [pgUser]);

    // Helper function to format quantity display
    const formatQuantity = (quantity) => {
        const num = Number(quantity);
        if (Number.isInteger(num)) {
            return num.toString();
        }
        return num.toFixed(2).replace(/\.?0+$/, '');
    };

    // Helper function to compare floating point numbers with tolerance
    // Returns true if a >= b within a small epsilon (0.01 for quantities)
    const isGreaterOrEqual = (a, b, epsilon = 0.01) => {
        return a >= b - epsilon;
    };

    // Helper function to show toast notifications
    const showToastNotification = (message, type = 'success') => {
        setToastMessage(message);
        setToastType(type);
        setShowToast(true);
        setTimeout(() => setShowToast(false), 3000);
    };

    // Fetch current meal plan
    const fetchCurrentMealPlan = useCallback(async () => {
        if (!pgUser?.id || !sessionToken) return null;

        try {
            const response = await fetchWithCSRF(`/api/v1/meal-plan/current/${pgUser.id}`, {
                headers: { 'Authorization': `Bearer ${sessionToken}` }
            });

            if (!response.ok) {
                throw new Error('Failed to fetch current meal plan');
            }

            const data = await response.json();
            console.log('[PantryAndGroceries.js] Current meal plan response:', data);
            // The response is an array with one item
            if (data && data.length > 0) {
                return data[0];
            }
            console.log('[PantryAndGroceries.js] No meal plan found');
            return null;
        } catch (err) {
            console.error('Error fetching current meal plan:', err);
            return null;
        }
    }, [pgUser?.id, sessionToken]);

    // Fetch required ingredients for meal plan
    const fetchMealPlanRequiredIngredients = useCallback(async (mealPlanId) => {
        if (!mealPlanId || !pgUser?.id || !sessionToken) return [];

        try {
            const response = await fetchWithCSRF(`/api/v1/meal-plan/${mealPlanId}/app_user/${pgUser.id}/required_ingredients`, {
                headers: { 'Authorization': `Bearer ${sessionToken}` }
            });

            if (!response.ok) {
                throw new Error('Failed to fetch meal plan required ingredients');
            }

            const data = await response.json();
            console.log('[PantryAndGroceries.js] Meal plan required ingredients:', data);
            return Array.isArray(data) ? data : [];
        } catch (err) {
            console.error('Error fetching meal plan required ingredients:', err);
            return [];
        }
    }, [pgUser?.id, sessionToken]);

    // Fetch current grocery list
    const fetchCurrentGroceryList = useCallback(async () => {
        if (!pgUser?.id || !sessionToken) return null;

        try {
            // Get today's date in YYYY-MM-DD format
            const today = getTodayLocalDateString();
            
            const response = await fetchWithCSRF(`/api/v1/grocery-list/current/${pgUser.id}/date/${today}`, {
                headers: { 'Authorization': `Bearer ${sessionToken}` }
            });

            if (!response.ok) {
                throw new Error('Failed to fetch current grocery list');
            }

            const data = await response.json();
            console.log('[PantryAndGroceries.js] Raw grocery list response:', data);
            // The response is an array with one item containing the data object
            if (data && data.length > 0 && data[0].data) {
                console.log('[PantryAndGroceries.js] Grocery list data:', data[0].data);
                console.log('[PantryAndGroceries.js] Grocery list ingredients:', data[0].data.grocery_list_ingredients);
                return data[0].data;
            }
            console.log('[PantryAndGroceries.js] No grocery list data found');
            return null;
        } catch (err) {
            console.error('Error fetching current grocery list:', err);
            return null;
        }
    }, [pgUser?.id, sessionToken]);

    // Fetch pantry ingredients
    const fetchPantryIngredients = useCallback(async () => {
        if (!pantryId || !pgUser?.id || !sessionToken) return [];

        try {
            const response = await fetchWithCSRF(`/api/v1/pantry-ingredient/pantry/${pantryId}/app_user/${pgUser.id}`, {
                headers: { 'Authorization': `Bearer ${sessionToken}` }
            });

            if (!response.ok) {
                throw new Error('Failed to fetch pantry ingredients');
            }

            const data = await response.json();
            return Array.isArray(data) ? data : [];
        } catch (err) {
            console.error('Error fetching pantry ingredients:', err);
            return [];
        }
    }, [pantryId, pgUser?.id, sessionToken]);

    // Combine pantry and meal plan required ingredients data
    const combineData = useCallback((pantryItems, groceryListData, mealPlanRequiredIngredients) => {
        console.log('[PantryAndGroceries.js] === COMBINE DATA DEBUG ===');
        console.log('[PantryAndGroceries.js] Pantry items count:', pantryItems?.length || 0);
        console.log('[PantryAndGroceries.js] Grocery list data:', groceryListData);
        console.log('[PantryAndGroceries.js] Meal plan required ingredients:', mealPlanRequiredIngredients);
        
        const groceryIngredients = groceryListData?.grocery_list_ingredients || [];
        console.log('[PantryAndGroceries.js] Grocery ingredients count:', groceryIngredients.length);
        console.log('[PantryAndGroceries.js] First grocery ingredient:', groceryIngredients[0]);
        
        // Create a map of meal plan required ingredients by ingredient_id (use this for required quantities)
        const mealPlanRequiredMap = new Map();
        (mealPlanRequiredIngredients || []).forEach(item => {
            mealPlanRequiredMap.set(item.ingredient_id, {
                quantity: item.quantity,
                ingredient_name: item.ingredient_name
            });
        });
        console.log('[PantryAndGroceries.js] Meal plan required map size:', mealPlanRequiredMap.size);
        
        // Create a map of grocery list ingredients by ingredient_id (use this for metadata like name, unit, food group)
        const groceryMap = new Map();
        groceryIngredients.forEach(item => {
            groceryMap.set(item.ingredient_id, {
                quantity: item.quantity,
                unit_of_measurement: item.unit_of_measurement,
                ingredient_name: item.ingredient_name,
                primary_food_group: item.primary_food_group
            });
        });
        console.log('[PantryAndGroceries.js] Grocery map size:', groceryMap.size);

        // Create a map of pantry ingredients by ingredient_id
        const pantryMap = new Map();
        pantryItems.forEach(item => {
            const existing = pantryMap.get(item.ingredient_id);
            if (existing) {
                // If ingredient exists in pantry multiple times, sum the quantities and costs
                pantryMap.set(item.ingredient_id, {
                    ...existing,
                    quantity: existing.quantity + parseFloat(item.quantity),
                    // Keep the first pantry ingredient id for editing purposes
                    pantry_ingredient_ids: [...existing.pantry_ingredient_ids, item.id],
                    // If any instance is confirmed, consider it confirmed
                    is_confirmed: existing.is_confirmed || item.is_confirmed,
                    // Sum the estimated costs
                    estimated_cost_in_cents: (existing.estimated_cost_in_cents || 0) + (item.estimated_cost_in_cents || 0)
                });
            } else {
                pantryMap.set(item.ingredient_id, {
                    ingredient_id: item.ingredient_id,
                    ingredient_name: item.ingredient_name,
                    quantity: parseFloat(item.quantity),
                    unit_of_measurement: item.unit_of_measurement,
                    primary_food_group: item.primary_food_group,
                    pantry_ingredient_ids: [item.id],
                    is_confirmed: item.is_confirmed || false,
                    estimated_cost_in_cents: item.estimated_cost_in_cents || 0
                });
            }
        });

        // Combine the data
        const combined = [];
        
        // Get all unique ingredient IDs from pantry, grocery list, and meal plan required ingredients
        const allIngredientIds = new Set([
            ...pantryMap.keys(),
            ...groceryMap.keys(),
            ...mealPlanRequiredMap.keys()
        ]);
        console.log('[PantryAndGroceries.js] All unique ingredient IDs count:', allIngredientIds.size);
        console.log('[PantryAndGroceries.js] Pantry map keys:', Array.from(pantryMap.keys()));
        console.log('[PantryAndGroceries.js] Grocery map keys:', Array.from(groceryMap.keys()));
        console.log('[PantryAndGroceries.js] Meal plan required map keys:', Array.from(mealPlanRequiredMap.keys()));

        allIngredientIds.forEach(ingredientId => {
            const pantryItem = pantryMap.get(ingredientId);
            const groceryItem = groceryMap.get(ingredientId);
            const mealPlanRequiredItem = mealPlanRequiredMap.get(ingredientId);
            
            const pantryQty = pantryItem?.quantity || 0;
            // Use meal plan required quantity as the true required quantity (not adjusted for pantry)
            const requiredQty = mealPlanRequiredItem?.quantity || 0;
            // Use tolerance-based comparison for floating point quantities
            const additionalNeeded = isGreaterOrEqual(pantryQty, requiredQty) ? 0 : Math.max(0, requiredQty - pantryQty);
            
            // Get ingredient_name from meal plan required data if available (since it contains all ingredients)
            const ingredientName = mealPlanRequiredItem?.ingredient_name || pantryItem?.ingredient_name || groceryItem?.ingredient_name;
            
            // Skip items without a name (data integrity issue)
            if (!ingredientName) {
                console.warn('[PantryAndGroceries.js] Skipping ingredient without name:', ingredientId);
                return;
            }
            
            // Calculate prorated estimated cost based on how much is needed vs how much is in pantry
            let estimatedCostForNeededQuantity = 0;
            if (pantryItem?.estimated_cost_in_cents && pantryQty > 0 && requiredQty > 0) {
                // If we have enough or more than needed, calculate cost of just the needed amount
                const quantityToUse = Math.min(pantryQty, requiredQty);
                estimatedCostForNeededQuantity = Math.round((pantryItem.estimated_cost_in_cents / pantryQty) * quantityToUse);
            }
            
            combined.push({
                ingredient_id: ingredientId,
                ingredient_name: ingredientName,
                pantry_quantity: pantryQty,
                grocery_quantity: requiredQty, // This is now the actual required quantity from meal plan
                additional_needed: additionalNeeded,
                unit_of_measurement: pantryItem?.unit_of_measurement || groceryItem?.unit_of_measurement,
                primary_food_group: pantryItem?.primary_food_group || groceryItem?.primary_food_group || 'Other',
                pantry_ingredient_ids: pantryItem?.pantry_ingredient_ids || [],
                is_needed_for_grocery: requiredQty > 0,
                is_confirmed: pantryItem?.is_confirmed || false,
                estimated_cost_in_cents: estimatedCostForNeededQuantity // Prorated cost for the quantity needed
            });
        });

        // Sort: ingredients needed for grocery list first, then by food group, then by name
        combined.sort((a, b) => {
            // First sort by whether it's needed for grocery list
            if (a.is_needed_for_grocery !== b.is_needed_for_grocery) {
                return b.is_needed_for_grocery ? 1 : -1;
            }
            // Then by food group
            if (a.primary_food_group !== b.primary_food_group) {
                return a.primary_food_group.localeCompare(b.primary_food_group);
            }
            // Finally by ingredient name
            return a.ingredient_name.localeCompare(b.ingredient_name);
        });

        console.log('[PantryAndGroceries.js] Combined data count:', combined.length);
        console.log('[PantryAndGroceries.js] First 3 combined items:', combined.slice(0, 3));
        console.log('[PantryAndGroceries.js] === END COMBINE DATA DEBUG ===');
        
        return combined;
    }, []);

    // Categorize ingredients into two sections
    const categorizeIngredients = useCallback((data) => {
        const neededThisWeek = [];
        const notUsedThisWeek = [];

        data.forEach(item => {
            if (item.grocery_quantity > 0) {
                // Items needed for recipes this week
                neededThisWeek.push(item);
            } else {
                // Items not needed for recipes this week
                notUsedThisWeek.push(item);
            }
        });

        return { neededThisWeek, notUsedThisWeek };
    }, []);

    // Fetch all data
    useEffect(() => {
        const fetchAllData = async () => {
            if (!pantryId || !pgUser?.id) return;

            setIsLoading(true);
            setError(null);

            try {
                // First fetch grocery list and pantry in parallel
                const [groceryData, pantryData] = await Promise.all([
                    fetchCurrentGroceryList(),
                    fetchPantryIngredients()
                ]);

                // Get meal plan ID from grocery list data
                const mealPlanId = groceryData?.grocery_list?.meal_plan_id;
                console.log('[PantryAndGroceries.js] Meal plan ID from grocery list:', mealPlanId);
                
                // Fetch meal plan required ingredients using the meal plan ID from grocery list
                const mealPlanRequiredIngredients = mealPlanId 
                    ? await fetchMealPlanRequiredIngredients(mealPlanId)
                    : [];

                setCurrentGroceryList(groceryData);
                setPantryIngredients(pantryData);

                const combined = combineData(pantryData, groceryData, mealPlanRequiredIngredients);
                setCombinedData(combined);

                // Store initial categorization (only on first load)
                const categorized = categorizeIngredients(combined);
                
                // Sort neededThisWeek alphabetically only (no status sorting for performance)
                const sortedNeededThisWeek = [...categorized.neededThisWeek].sort((a, b) => 
                    a.ingredient_name.localeCompare(b.ingredient_name)
                );
                
                setInitialCategories({
                    neededThisWeek: sortedNeededThisWeek,
                    notUsedThisWeek: categorized.notUsedThisWeek
                });
                
                // Auto-check ingredients that have been confirmed and set their button states
                const confirmedIngredients = sortedNeededThisWeek.filter(item => item.is_confirmed);
                const confirmedIngredientIds = confirmedIngredients.map(item => item.ingredient_id);
                setCheckedIngredients(new Set(confirmedIngredientIds));
                
                // Initialize button states for confirmed ingredients
                const initialButtonStates = new Map();
                confirmedIngredients.forEach(item => {
                    if (isGreaterOrEqual(item.pantry_quantity, item.grocery_quantity) && item.grocery_quantity > 0) {
                        initialButtonStates.set(item.ingredient_id, 'enough');
                    } else if (item.pantry_quantity === 0) {
                        initialButtonStates.set(item.ingredient_id, 'none');
                    } //else if (item.pantry_quantity > 0 && item.pantry_quantity < item.grocery_quantity) {
                    //    initialButtonStates.set(item.ingredient_id, 'some');
                    //}
                });
                setButtonStates(initialButtonStates);
            } catch (err) {
                console.error('Error fetching data:', err);
                setError('Failed to load data. Please refresh and try again.');
            } finally {
                setIsLoading(false);
            }
        };

        fetchAllData();
    }, [pantryId, pgUser?.id, fetchMealPlanRequiredIngredients, fetchCurrentGroceryList, fetchPantryIngredients, combineData, categorizeIngredients]);

    // Get current data with updated quantities from combinedData
    // But keep the initial category assignments (items stay in their original sections)
    const getCategorizedDataWithUpdates = () => {
        if (!initialCategories) {
            return { neededThisWeek: [], notUsedThisWeek: [] };
        }

        // Create a map of current data by ingredient_id for quick lookup
        const currentDataMap = new Map();
        combinedData.forEach(item => {
            currentDataMap.set(item.ingredient_id, item);
        });

        // Filter by search query and update with current quantities
        const filterAndUpdate = (items) => {
            return items
                .map(item => currentDataMap.get(item.ingredient_id) || item)
                .filter(item => 
                    item?.ingredient_name?.toLowerCase().includes(searchQuery.toLowerCase()) ?? false
                );
        };

        return {
            neededThisWeek: filterAndUpdate(initialCategories.neededThisWeek),
            notUsedThisWeek: filterAndUpdate(initialCategories.notUsedThisWeek)
        };
    };

    const { neededThisWeek, notUsedThisWeek } = getCategorizedDataWithUpdates();

    // Calculate progress (count checked items with "Enough" status)
    const totalNeeded = neededThisWeek.length;
    const itemsWithEnough = neededThisWeek.filter(item => 
        checkedIngredients.has(item.ingredient_id) &&
        isGreaterOrEqual(item.pantry_quantity, item.grocery_quantity) && 
        item.grocery_quantity > 0
    ).length;
    const progressPercentage = totalNeeded > 0 ? Math.round((itemsWithEnough / totalNeeded) * 100) : 0;

    // Calculate total savings (only for confirmed items with "Enough" status)
    const ingredientsWithSavings = neededThisWeek.filter(item => {
        const hasEnoughStatus = buttonStates.get(item.ingredient_id) === 'enough';
        const isConfirmed = checkedIngredients.has(item.ingredient_id);
        return hasEnoughStatus && isConfirmed && item.estimated_cost_in_cents > 0;
    });
    
    const totalSavingsCents = ingredientsWithSavings.reduce((sum, item) => {
        return sum + item.estimated_cost_in_cents;
    }, 0);
    const totalSavingsDollars = (totalSavingsCents / 100).toFixed(2);
    const ingredientsWithSavingsCount = ingredientsWithSavings.length;

    // Group "Not Used This Week" by food group (keep grouping for that section)
    const groupByFoodGroup = (items) => {
        return items.reduce((acc, item) => {
            const group = item.primary_food_group || 'Other';
            if (!acc[group]) acc[group] = [];
            acc[group].push(item);
            return acc;
        }, {});
    };

    // Don't group neededThisWeek - keep status sorting intact
    const notUsedGrouped = groupByFoodGroup(notUsedThisWeek);

    // Determine button state based on pantry vs required quantity (only if checked by user)
    const getButtonState = (item) => {
        // First check if we have an explicit button state set (from user click)
        if (buttonStates.has(item.ingredient_id)) {
            const explicitState = buttonStates.get(item.ingredient_id);
            // Only log for Paprika to avoid spam
            if (item.ingredient_name === 'Paprika') {
                console.log(`[getButtonState] ${item.ingredient_name} has explicit state:`, explicitState);
            }
            return explicitState;
        }
        
        // Only show active state if user has checked this ingredient
        if (!checkedIngredients.has(item.ingredient_id)) {
            return null;
        }
        
        // Calculate based on data (with tolerance for floating point comparison)
        const hasEnough = isGreaterOrEqual(item.pantry_quantity, item.grocery_quantity);
        const isZero = item.pantry_quantity === 0;
        const hasRequirement = item.grocery_quantity > 0;
        
        // Only log for Paprika to avoid spam
        if (item.ingredient_name === 'Paprika') {
            console.log(`[getButtonState] ${item.ingredient_name} calculating state:`, {
                pantry_quantity: item.pantry_quantity,
                grocery_quantity: item.grocery_quantity,
                hasEnough,
                isZero,
                hasRequirement,
                isChecked: checkedIngredients.has(item.ingredient_id)
            });
        }
        
        if (hasEnough && hasRequirement) {
            return 'enough'; // Green
        } else if (isZero) {
            return 'none'; // Red
        }
        return null;
    };

    // Handle button clicks
    const handleButtonClick = async (item, buttonType) => {
        // IMMEDIATELY set ONLY the button state for instant UI feedback
        setButtonStates(prev => {
            const newMap = new Map(prev);
            newMap.set(item.ingredient_id, buttonType);
            return newMap;
        });
        
        // Use setTimeout to ensure button renders before we do heavy data updates
        setTimeout(async () => {
            if (buttonType === 'enough') {
                const newQuantity = item.grocery_quantity;
                
                // Optimistically update the data
                setCombinedData(prev => prev.map(i => 
                    i.ingredient_id === item.ingredient_id
                        ? { 
                            ...i, 
                            pantry_quantity: newQuantity,
                            additional_needed: isGreaterOrEqual(newQuantity, i.grocery_quantity) ? 0 : Math.max(0, i.grocery_quantity - newQuantity),
                            is_confirmed: true
                          }
                        : i
                ));
                
                // Mark ingredient as checked
                setCheckedIngredients(prev => new Set(prev).add(item.ingredient_id));
                
                // Call the new dedicated hook
                try {
                    console.log(`[handleButtonClick ENOUGH] Calling API for ${item.ingredient_name}:`, {
                        ingredient_id: item.ingredient_id,
                        newQuantity,
                        unit: item.unit_of_measurement
                    });
                    
                    const result = await confirmHasEnough(
                        item.ingredient_id,
                        pantryId,
                        pgUser.id,
                        newQuantity,
                        item.unit_of_measurement
                    );
                    
                    console.log(`[handleButtonClick ENOUGH] API result for ${item.ingredient_name}:`, result);
                    
                    // Update pantry_ingredient_ids if we created a new record
                    if (result && result.id && item.pantry_ingredient_ids.length === 0) {
                        setCombinedData(prev => prev.map(i => 
                            i.ingredient_id === item.ingredient_id
                                ? { ...i, pantry_ingredient_ids: [result.id] }
                                : i
                        ));
                    }
                    
                    // Refresh grocery list data (will update the drawer if it's open)
                    console.log(`[handleButtonClick ENOUGH] About to refresh data for ${item.ingredient_name}...`);
                    await handleItemsAddedToPantry();
                    console.log(`[handleButtonClick ENOUGH] Data refresh complete for ${item.ingredient_name}`);
                    
                    // Force grocery drawer to refresh by changing its key
                    setGroceryDrawerRefreshKey(prev => prev + 1);
                    
                    showToastNotification(item.ingredient_name + ' removed from grocery list'); //'Removed from grocery list'
                } catch (error) {
                    // If API call fails, revert the optimistic update
                    console.error('Failed to confirm quantity:', error);
                    setCombinedData(prev => prev.map(i => 
                        i.ingredient_id === item.ingredient_id
                            ? { 
                                ...i, 
                                pantry_quantity: item.pantry_quantity,
                                additional_needed: item.additional_needed,
                                is_confirmed: item.is_confirmed
                              }
                            : i
                    ));
                    // Revert button state
                    setButtonStates(prev => {
                        const newMap = new Map(prev);
                        newMap.delete(item.ingredient_id);
                        return newMap;
                    });
                    setCheckedIngredients(prev => {
                        const newSet = new Set(prev);
                        newSet.delete(item.ingredient_id);
                        return newSet;
                    });
                    showToastNotification('Failed to confirm quantity', 'error');
                }
            } else if (buttonType === 'none') {
                const newQuantity = 0;
                
                // Optimistically update the data
                setCombinedData(prev => prev.map(i => 
                    i.ingredient_id === item.ingredient_id
                        ? { 
                            ...i, 
                            pantry_quantity: newQuantity,
                            additional_needed: i.grocery_quantity,
                            is_confirmed: true
                          }
                        : i
                ));
                
                // Mark ingredient as checked
                setCheckedIngredients(prev => new Set(prev).add(item.ingredient_id));
                
                // Call the new dedicated hook
                try {
                    const result = await confirmHasLessThanEnough(
                        item.ingredient_id,
                        pantryId,
                        pgUser.id,
                        newQuantity,
                        item.unit_of_measurement
                    );
                    
                    // Update pantry_ingredient_ids if we created a new record
                    if (result && result.id && item.pantry_ingredient_ids.length === 0) {
                        setCombinedData(prev => prev.map(i => 
                            i.ingredient_id === item.ingredient_id
                                ? { ...i, pantry_ingredient_ids: [result.id] }
                                : i
                        ));
                    }
                    
                    // Refresh grocery list data (will update the drawer if it's open)
                    await handleItemsAddedToPantry();
                    
                    // Force grocery drawer to refresh by changing its key
                    setGroceryDrawerRefreshKey(prev => prev + 1);
                    
                    showToastNotification(item.ingredient_name + ' added to grocery list'); //'Pantry quantity confirmed as 0'
                } catch (error) {
                    // If API call fails, revert the optimistic update
                    console.error('Failed to confirm quantity:', error);
                    setCombinedData(prev => prev.map(i => 
                        i.ingredient_id === item.ingredient_id
                            ? { 
                                ...i, 
                                pantry_quantity: item.pantry_quantity,
                                additional_needed: item.additional_needed,
                                is_confirmed: item.is_confirmed
                              }
                            : i
                    ));
                    // Revert button state
                    setButtonStates(prev => {
                        const newMap = new Map(prev);
                        newMap.delete(item.ingredient_id);
                        return newMap;
                    });
                    setCheckedIngredients(prev => {
                        const newSet = new Set(prev);
                        newSet.delete(item.ingredient_id);
                        return newSet;
                    });
                    showToastNotification('Failed to confirm quantity', 'error');
                }
            } 
            // COMMENTED OUT: "Some" button functionality - simplified to just Enough/None
            // else if (buttonType === 'some') {
            //     // Mark ingredient as checked before opening input
            //     setCheckedIngredients(prev => new Set(prev).add(item.ingredient_id));
            //     // Open input to enter quantity
            //     handleQuantityEdit(item);
            // }
        }, 0);
    };

    // Render ingredient item (reusable component)
    const renderIngredientItem = (item, borderColor = 'transparent', showButtons = false) => {
        const currentState = getButtonState(item);
        
        return (
            <div
                key={item.ingredient_id}
                className="d-flex justify-content-between align-items-center px-1 py-2"
                style={{
                    borderBottom: '1px solid #e0e0e0',
                    marginBottom: '4px'
                }}
            >
                {/* Column 1: Ingredient Name */}
                <div style={{ width: '40%', textAlign: 'left' }} className="d-flex align-items-start flex-column">
                    <div className="d-flex align-items-center">
                        <img
                            src={getIngredientImageUrl(item.ingredient_id)}
                            alt={item.ingredient_name}
                            style={{
                                width: '1.25rem',
                                height: '1.25rem',
                                borderRadius: '4px',
                                marginRight: '0.5rem',
                                objectFit: 'cover'
                            }}
                            onError={e => {
                                e.target.onerror = null;
                                e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIGZpbGw9IiNFMEUwRTAiLz48L3N2Zz4=';
                            }}
                        />
                        <span style={{ fontWeight: '500', fontSize: '0.9rem', textAlign: 'left' }}>
                            {item.ingredient_name}
                        </span>
                    </div>
                    {!showButtons && (
                        <span 
                            onClick={() => {
                                setSelectedIngredientForRecipes({
                                    id: item.ingredient_id,
                                    name: item.ingredient_name
                                });
                                setIsRecipeDrawerOpen(true);
                            }}
                            style={{ 
                                fontSize: '0.75rem', 
                                color: '#52cd92', 
                                cursor: 'pointer',
                                textDecoration: 'none',
                                fontWeight: '500'
                            }}
                            onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
                            onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
                        >
                            See Recipes
                        </span>
                    )}
                </div>

                {/* Column 2: Required Quantity or Pantry Quantity */}
                <div style={{ width: '30%', textAlign: 'center' }}>
                    <div className="d-flex flex-column align-items-center justify-content-center">
                        <div className="d-flex align-items-baseline gap-1">
                            <span style={{ fontWeight: '500', fontSize: '0.95rem' }}>
                                {/* Show grocery_quantity for "needed this week", pantry_quantity for "other items" */}
                                {formatQuantity(showButtons ? item.grocery_quantity : item.pantry_quantity)}
                            </span>
                            <span className="text-muted" style={{ fontSize: '0.75rem' }}>
                                {item.unit_of_measurement}
                            </span>
                        </div>
                        {showButtons && (
                            <span 
                                style={{ 
                                    fontSize: '0.7rem', 
                                    color: '#999',
                                    fontStyle: 'italic',
                                    marginTop: '4px'
                                }}
                            >
                                We think you have {formatQuantity(item.pantry_quantity)} {item.unit_of_measurement}
                            </span>
                        )}
                    </div>
                </div>

                {/* Column 3: I Have (Three Buttons) */}
                <div style={{ width: '40%', textAlign: 'right' }}>
                    {showButtons && editingQuantityId === item.ingredient_id ? (
                        <div className="d-flex align-items-center justify-content-center" style={{ position: 'relative' }}>
                            <input
                                type="number"
                                className="form-control-custom form-control-sm"
                                value={editingQuantity}
                                onChange={(e) => handleQuantityInputChange(e.target.value)}
                                onBlur={() => handleQuantitySubmit(item)}
                                onKeyDown={(e) => handleQuantityKeyPress(e, item)}
                                autoFocus
                                min="0"
                                step="0.25"
                                style={{ width: '90px', textAlign: 'center' }}
                                placeholder="Enter qty"
                            />
                            <span className="text-muted small" style={{ 
                                fontSize: '0.75rem', 
                                position: 'absolute',
                                left: '50%',
                                marginLeft: '53px'
                            }}>
                                {item.unit_of_measurement}
                            </span>
                        </div>
                    ) : showButtons ? (
                        <>
                        <div className="d-flex flex-column gap-1 align-items-center">
                            {/* Green Button - I have enough */}
                            <div className="d-flex align-items-center gap-2">
                                <button
                                    onClick={() => handleButtonClick(item, 'enough')}
                                    style={{
                                        fontSize: '0.75rem',
                                        color: currentState === 'enough' ? '#52cd92' : '#495057',
                                        backgroundColor: currentState === 'enough' ? 'f0fdf7' : 'white',
                                        border: currentState === 'enough' ? '1.5px solid #52cd92' : '1.5px solid #dddddd',
                                        borderRadius: '6px',
                                        cursor: 'pointer',
                                        padding: '4px 12px',
                                        fontWeight: '600',
                                        transition: 'all 0.2s ease',
                                        boxShadow: currentState === 'enough' ? '0 2px 4px rgba(82, 205, 146, 0.3)' : 'none',
                                        minWidth: '90px'
                                    }}
                                >
                                    Enough
                                </button>
                                {checkedIngredients.has(item.ingredient_id) && currentState === 'enough' && item.additional_needed === 0 ? (
                                    <img
                                        src="/icons/checkmark-solid.svg"
                                        alt="Checkmark"
                                        style={{
                                            width: '14px',
                                            height: '14px'
                                        }}
                                    />
                                ) : (
                                    <div style={{ width: '14px', height: '14px' }} />
                                )}
                            </div>

                            {/* COMMENTED OUT: "Some" button - simplified to just Enough/None */}
                            {/* Yellow Button - I have some */}
                            {/* <button
                                onClick={() => handleButtonClick(item, 'some')}
                                style={{
                                    fontSize: '0.75rem',
                                    color: currentState === 'some' ? '#ffa500' : '#495057',
                                    backgroundColor: currentState === 'some' ? '#fff9f0' : 'white',
                                    border: currentState === 'some' ? '1.5px solid #ffa500' : '1.5px solid #dddddd',
                                    borderRadius: '6px',
                                    cursor: 'pointer',
                                    padding: '4px 16px',
                                    fontWeight: '600',
                                    transition: 'all 0.2s ease',
                                    boxShadow: currentState === 'some' ? '0 2px 4px rgba(255, 165, 0, 0.3)' : 'none',
                                    minWidth: '90px'
                                }}
                            >
                                Some
                            </button> */}

                            {/* Red Button - I don't have any */}
                            <div className="d-flex align-items-center gap-2">
                                <button
                                    onClick={() => handleButtonClick(item, 'none')}
                                    style={{
                                        fontSize: '0.75rem',
                                        color: currentState === 'none' ? '#dc3545' : '#495057',
                                        backgroundColor: currentState === 'none' ? 'fef2f2' : 'white',
                                        border: currentState === 'none' ? '1.5px solid #dc3545' : '1.5px solid #dddddd',
                                        borderRadius: '6px',
                                        cursor: 'pointer',
                                        padding: '4px 12px',
                                        fontWeight: '600',
                                        transition: 'all 0.2s ease',
                                        boxShadow: currentState === 'none' ? '0 2px 4px rgba(220, 53, 69, 0.3)' : 'none',
                                        minWidth: '90px'
                                    }}
                                >
                                    Not Enough
                                </button>
                                {checkedIngredients.has(item.ingredient_id) && currentState === 'none' && item.additional_needed > 0 ? (
                                    <img
                                        src="/icons/shopping-cart-solid_orange.svg"
                                        alt="Shopping Cart"
                                        style={{
                                            width: '14px',
                                            height: '14px'
                                        }}
                                    />
                                ) : (
                                    <div style={{ width: '14px', height: '14px' }} />
                                )}
                            </div>
                        </div>
                    </>
                    ) : (
                        <span className="text-muted" style={{ fontSize: '0.85rem' }}>-</span>
                    )}
                </div>
            </div>
        );
    };

    // Handle quantity edit
    const handleQuantityEdit = (item) => {
        setEditingQuantityId(item.ingredient_id);
        setEditingQuantity(formatQuantity(item.pantry_quantity));
    };

    // Handle quantity change with +/- buttons
    const handleQuantityChange = async (item, delta) => {
        const newQuantity = Math.max(0, item.pantry_quantity + delta);
        await updateQuantity(item, newQuantity);
    };

    // Handle manual quantity input
    const handleQuantityInputChange = (value) => {
        setEditingQuantity(value);
    };

    // Update quantity in database
    const updateQuantity = async (item, newQuantity, skipStateUpdate = false) => {
        try {
            // Check if ingredient exists in pantry
            if (item.pantry_ingredient_ids.length === 0) {
                // Ingredient doesn't exist in pantry yet - create it
                console.log('Creating new pantry ingredient:', {
                    pantry_id: pantryId,
                    ingredient_id: item.ingredient_id,
                    unit_of_measurement: item.unit_of_measurement,
                    quantity: Number(newQuantity)
                });

                const result = await addPantryIngredient({
                    pantry_id: pantryId,
                    ingredient_id: item.ingredient_id,
                    unit_of_measurement: item.unit_of_measurement,
                    quantity: Number(newQuantity),
                    is_confirmed: true,
                    confirmed_at: new Date().toISOString()
                });

                if (result && result.data) {
                    // Only update local state if we haven't already done an optimistic update
                    if (!skipStateUpdate) {
                        setCombinedData(prev => prev.map(i => 
                            i.ingredient_id === item.ingredient_id
                                ? { 
                                    ...i, 
                                    pantry_quantity: Number(newQuantity),
                                    additional_needed: isGreaterOrEqual(Number(newQuantity), i.grocery_quantity) ? 0 : Math.max(0, i.grocery_quantity - Number(newQuantity)),
                                    pantry_ingredient_ids: [result.data.id],
                                    is_confirmed: true
                                  }
                                : i
                        ));
                        showToastNotification('Added to pantry');
                    } else {
                        // Still update the pantry_ingredient_ids since we now have the ID from the API
                        setCombinedData(prev => prev.map(i => 
                            i.ingredient_id === item.ingredient_id
                                ? { 
                                    ...i, 
                                    pantry_ingredient_ids: [result.data.id]
                                  }
                                : i
                        ));
                    }
                } else {
                    throw new Error('Failed to add to pantry');
                }
            } else {
                // Ingredient exists - update it
                const result = await updatePantryIngredient(
                    item.pantry_ingredient_ids[0],
                    pantryId,
                    pgUser.id,
                    { 
                        quantity: Number(newQuantity),
                        is_confirmed: true
                    }
                );

                if (result) {
                    // Only update local state if we haven't already done an optimistic update
                    if (!skipStateUpdate) {
                        setCombinedData(prev => prev.map(i => 
                            i.ingredient_id === item.ingredient_id
                                ? { 
                                    ...i, 
                                    pantry_quantity: Number(newQuantity),
                                    additional_needed: isGreaterOrEqual(Number(newQuantity), i.grocery_quantity) ? 0 : Math.max(0, i.grocery_quantity - Number(newQuantity)),
                                    is_confirmed: true
                                  }
                                : i
                        ));
                        showToastNotification('Pantry quantity updated');
                    }
                } else {
                    throw new Error('Failed to update quantity');
                }
            }
        } catch (error) {
            console.error('Error updating/adding quantity:', error);
            // Re-throw the error so the caller can handle it (for optimistic update rollback)
            throw error;
        }
    };

    // Handle quantity submit (when user finishes editing)
    const handleQuantitySubmit = async (item) => {
        const newQuantity = parseFloat(editingQuantity);
        if (isNaN(newQuantity) || newQuantity < 0) {
            showToastNotification('Please enter a valid quantity', 'error');
            setEditingQuantityId(null);
            return;
        }

        // Determine which button state this quantity corresponds to
        // Simplified to Enough/None only
        let buttonState = 'none'; // Default to 'none' if not enough
        if (newQuantity >= item.grocery_quantity && item.grocery_quantity > 0) {
            buttonState = 'enough';
        } else if (newQuantity === 0) {
            buttonState = 'none';
        }
        // Note: Any quantity between 0 and grocery_quantity will be treated as 'none' (need to buy more)
        
        // IMMEDIATELY set ONLY the button state for instant UI feedback
        setButtonStates(prev => {
            const newMap = new Map(prev);
            newMap.set(item.ingredient_id, buttonState);
            return newMap;
        });
        setEditingQuantityId(null);

        // Use setTimeout to ensure button renders before we do heavy data updates
        setTimeout(() => {
            // Optimistically update the data
            setCombinedData(prev => prev.map(i => 
                i.ingredient_id === item.ingredient_id
                    ? { 
                        ...i, 
                        pantry_quantity: newQuantity,
                        additional_needed: isGreaterOrEqual(newQuantity, i.grocery_quantity) ? 0 : Math.max(0, i.grocery_quantity - newQuantity),
                        is_confirmed: true
                      }
                    : i
            ));
            
            // Mark ingredient as checked
            setCheckedIngredients(prev => new Set(prev).add(item.ingredient_id));
            
            // Then handle the API call in the background
            updateQuantity(item, newQuantity, true).catch(error => {
                // If API call fails, revert the optimistic update
                console.error('Failed to update quantity:', error);
                setCombinedData(prev => prev.map(i => 
                    i.ingredient_id === item.ingredient_id
                        ? { 
                            ...i, 
                            pantry_quantity: item.pantry_quantity,
                            additional_needed: item.additional_needed,
                            is_confirmed: item.is_confirmed
                          }
                        : i
                ));
                // Revert button state
                setButtonStates(prev => {
                    const newMap = new Map(prev);
                    newMap.delete(item.ingredient_id);
                    return newMap;
                });
                showToastNotification('Failed to update quantity', 'error');
            });
            showToastNotification('Pantry quantity updated');
        }, 0);
    };

    // Handle key press in quantity input
    const handleQuantityKeyPress = (e, item) => {
        if (e.key === 'Enter') {
            handleQuantitySubmit(item);
        } else if (e.key === 'Escape') {
            setEditingQuantityId(null);
        }
    };

    // Format date range for display
    const formatDateRange = (startDate, endDate) => {
        if (!startDate || !endDate) return '';
        
        const start = new Date(startDate);
        const end = new Date(endDate);
        
        const formatDate = (date) => {
            const month = date.toLocaleString('en-US', { month: 'short' });
            const day = date.getDate();
            return `${month} ${day}`;
        };
        
        return `${formatDate(start)} - ${formatDate(end)}`;
    };

    // Handle items added to pantry from drawer
    const handleItemsAddedToPantry = useCallback(async () => {
        console.log('[handleItemsAddedToPantry] Starting data refresh...');
        
        // Refetch pantry and grocery list
        const [groceryData, pantryData] = await Promise.all([
            fetchCurrentGroceryList(),
            fetchPantryIngredients()
        ]);

        // Get meal plan ID from grocery list data
        const mealPlanId = groceryData?.grocery_list?.meal_plan_id;
        
        // Fetch meal plan required ingredients using the meal plan ID from grocery list
        const mealPlanRequiredIngredients = mealPlanId 
            ? await fetchMealPlanRequiredIngredients(mealPlanId)
            : [];

        setCurrentGroceryList(groceryData);
        setPantryIngredients(pantryData);

        const combined = combineData(pantryData, groceryData, mealPlanRequiredIngredients);
        setCombinedData(combined);
        
        // Re-categorize and reinitialize button states (same as initial load)
        const categorized = categorizeIngredients(combined);
        const sortedNeededThisWeek = [...categorized.neededThisWeek].sort((a, b) => 
            a.ingredient_name.localeCompare(b.ingredient_name)
        );
        
        // Update initial categories
        setInitialCategories({
            neededThisWeek: sortedNeededThisWeek,
            notUsedThisWeek: categorized.notUsedThisWeek
        });
        
        // Merge checked ingredients: keep existing and add new confirmed ones
        const confirmedIngredients = sortedNeededThisWeek.filter(item => item.is_confirmed);
        const confirmedIngredientIds = confirmedIngredients.map(item => item.ingredient_id);
        setCheckedIngredients(prev => {
            const merged = new Set(prev); // Start with existing
            confirmedIngredientIds.forEach(id => merged.add(id)); // Add new confirmed ones
            return merged;
        });
        
        console.log('[handleItemsAddedToPantry] Confirmed ingredients:', confirmedIngredients.map(i => ({
            name: i.ingredient_name,
            pantry_qty: i.pantry_quantity,
            required_qty: i.grocery_quantity,
            is_confirmed: i.is_confirmed
        })));
        
        // Merge button states: keep existing states and update/add new ones from confirmed ingredients
        setButtonStates(prevButtonStates => {
            const refreshedButtonStates = new Map(prevButtonStates); // Start with existing states
            
            confirmedIngredients.forEach(item => {
                const hasEnough = isGreaterOrEqual(item.pantry_quantity, item.grocery_quantity);
                const isZero = item.pantry_quantity === 0;
                const hasRequirement = item.grocery_quantity > 0;
                
                console.log(`[handleItemsAddedToPantry] Evaluating ${item.ingredient_name}:`, {
                    pantry_quantity: item.pantry_quantity,
                    grocery_quantity: item.grocery_quantity,
                    hasEnough,
                    isZero,
                    hasRequirement,
                    is_confirmed: item.is_confirmed
                });
                
                if (hasEnough && hasRequirement) {
                    refreshedButtonStates.set(item.ingredient_id, 'enough');
                    console.log(`[handleItemsAddedToPantry] ✓ Setting ${item.ingredient_name} to 'enough'`);
                } else if (isZero) {
                    refreshedButtonStates.set(item.ingredient_id, 'none');
                    console.log(`[handleItemsAddedToPantry] ✓ Setting ${item.ingredient_name} to 'none'`);
                } else {
                    console.log(`[handleItemsAddedToPantry] ⚠ ${item.ingredient_name} doesn't match any button state criteria`);
                }
            });
            
            console.log('[handleItemsAddedToPantry] Final button states:', Array.from(refreshedButtonStates.entries()));
            return refreshedButtonStates;
        });
        
        console.log('[handleItemsAddedToPantry] Data refresh complete');
    }, [fetchMealPlanRequiredIngredients, fetchCurrentGroceryList, fetchPantryIngredients, combineData, categorizeIngredients]);

    return (
        <ErrorBoundary fallbackMessage="Unable to load pantry and groceries. Please try again later.">
            <div className="container">
                {isAuthLoading ? (
                    <div className="text-center my-4">
                        <div className="spinner-border" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                    </div>
                ) : !pgUser ? (
                    <div className="alert alert-warning">
                        <p className="h5 mb-3">Unable to connect to the server. Please try again later or send a message to <a href="mailto:support@getautochef.com">support@getautochef.com</a></p>
                        <Button onClick={() => window.location.reload()}>Retry</Button>
                    </div>
                ) : (
                    <>
                        <div className="row mb-3">
                            <div className="col text-center">
                                {/* Search Bar */}
                                {/* <div className="position-relative" style={{ maxWidth: '400px', margin: '0 auto' }}>
                                    <svg 
                                        width="16" 
                                        height="16" 
                                        viewBox="0 0 16 16" 
                                        fill="currentColor"
                                        style={{ 
                                            position: 'absolute', 
                                            left: '12px', 
                                            top: '37%', 
                                            transform: 'translateY(-50%)',
                                            opacity: 0.4,
                                            color: '#999',
                                            pointerEvents: 'none'
                                        }}
                                    >
                                        <path d="M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398h-.001c.03.04.062.078.098.115l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85a1.007 1.007 0 0 0-.115-.1zM12 6.5a5.5 5.5 0 1 1-11 0 5.5 5.5 0 0 1 11 0z"/>
                                    </svg>
                                    <input
                                        type="text"
                                        placeholder="Search pantry"
                                        value={searchQuery}
                                        onChange={e => setSearchQuery(e.target.value)}
                                        className="form-control-custom mb-3"
                                        style={{
                                            width: '100%',
                                            fontSize: '0.9rem',
                                            padding: '0.5rem 0.5rem 0.5rem 36px',
                                            backgroundColor: '#fafafa',
                                            border: '1px solid #e0e0e0',
                                            color: '#666'
                                        }}
                                    />
                                </div> */}

                                {/* Date Range Display */}
                                {currentGroceryList?.grocery_list && (
                                    <div className="mb-1" style={{ position: 'relative', textAlign: 'center' }}>
                                        <h4 className="mb-0" style={{ fontWeight: 'bold', display: 'inline-block', position: 'relative' }}>
                                            {formatDateRange(
                                                currentGroceryList.grocery_list.start_date,
                                                currentGroceryList.grocery_list.end_date
                                            )}
                                        </h4>
                                    </div>
                                )}
                            </div>
                        </div>

                        {isLoading ? (
                            <div className="text-center my-4">
                                <div className="spinner-border" role="status" style={{ color: '#52cd92' }}>
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                            </div>
                        ) : error ? (
                            <div className="alert alert-danger" role="alert">
                                {error}
                            </div>
                        ) : (
                            <div className="row justify-content-center">
                                <div className="col-12 pantry-content-with-sticky-button" style={{ maxWidth: '800px', paddingBottom: '100px' }}>
                                    {/* Two Category Sections */}
                                    {(neededThisWeek.length === 0 && notUsedThisWeek.length === 0) ? (
                                        <div className="text-center py-5">
                                            <p className="text-muted">No ingredients found</p>
                                        </div>
                                    ) : (
                                        <>
                                            {/* Section 1: Check Your Pantry */}
                                            {neededThisWeek.length > 0 && (
                                                <div className="mb-5">
                                                    {/* <h3 style={{ 
                                                        fontWeight: '700', 
                                                        marginBottom: '1rem',
                                                        textAlign: 'center',
                                                        color: '#52cd92',
                                                        fontSize: '1.5rem'
                                                    }}>
                                                        Check Your Pantry
                                                    </h3> */}
                                                    {/* <p style={{ textAlign: 'center', color: '#666', marginBottom: '1rem', fontSize: '0.9rem' }}>
                                                        These ingredients are needed for this week's recipes. Let AutoChef know how much you have of each ingredient.
                                                    </p> */}
                                                    
                                                    {/* Progress Bar */}
                                                    <div style={{ 
                                                        maxWidth: '600px', 
                                                        margin: '0 auto 1.5rem',
                                                        padding: '0 20px'
                                                    }}>
                                                        {/* <div style={{ 
                                                            display: 'flex', 
                                                            justifyContent: 'space-between', 
                                                            alignItems: 'center',
                                                            marginBottom: '8px'
                                                        }}>
                                                            <span style={{ 
                                                                fontSize: '0.9rem', 
                                                                fontWeight: '600',
                                                                color: progressPercentage === 100 ? '#52cd92' : '#666'
                                                            }}>
                                                                You already have {itemsWithEnough} of {totalNeeded} ingredients for this week
                                                            </span>
                                                            <span style={{ 
                                                                fontSize: '1rem', 
                                                                fontWeight: '700',
                                                                color: '#52cd92'
                                                            }}>
                                                                {progressPercentage}%
                                                            </span>
                                                        </div>
                                                        <div style={{
                                                            width: '100%',
                                                            height: '12px',
                                                            backgroundColor: '#e0e0e0',
                                                            borderRadius: '12px',
                                                            overflow: 'hidden',
                                                            boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.1)'
                                                        }}>
                                                            <div style={{
                                                                width: `${progressPercentage}%`,
                                                                height: '100%',
                                                                backgroundColor: progressPercentage === 100 ? '#45b880' : '#52cd92',
                                                                transition: 'width 0.5s ease, background-color 0.3s ease',
                                                                boxShadow: progressPercentage > 0 ? '0 0 8px rgba(82, 205, 146, 0.4)' : 'none'
                                                            }} />
                                                        </div>
                                                        {progressPercentage === 100 && (
                                                            <div style={{
                                                                textAlign: 'center',
                                                                marginTop: '8px',
                                                                fontSize: '0.85rem',
                                                                color: '#52cd92',
                                                                fontWeight: '600'
                                                            }}>
                                                                🎉 All set! You have everything you need.
                                                            </div>
                                                        )} */}
                                                        
                                                        {/* Savings Display */}
                                                        {totalSavingsCents > 0 && (
                                                            <div style={{
                                                                textAlign: 'center',
                                                                // marginTop: '12px',
                                                                padding: '8px 16px',
                                                                backgroundColor: '#f0fdf7',
                                                                border: '1.5px solid #52cd92',
                                                                borderRadius: '6px',
                                                                boxShadow: '0 1px 3px rgba(82, 205, 146, 0.1)'
                                                            }}>
                                                                <div style={{
                                                                    fontSize: '0.8rem',
                                                                    color: '#666',
                                                                    marginBottom: '2px'
                                                                }}>
                                                                    You're saving by using <b>{ingredientsWithSavingsCount}</b> {ingredientsWithSavingsCount === 1 ? 'ingredient' : 'ingredients'} you already have
                                                                </div>
                                                                <div style={{
                                                                    fontSize: '1.25rem',
                                                                    fontWeight: '700',
                                                                    color: '#52cd92'
                                                                }}>
                                                                    ${totalSavingsDollars}
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                    
                                                    {/* Column Headers for this section */}
                                                    <div className="d-flex justify-content-between align-items-center px-3 py-2 mb-2" 
                                                         style={{ 
                                                             fontWeight: '600', 
                                                             borderBottom: '2px solid #52cd92',
                                                             fontSize: '0.9rem'
                                                         }}>
                                                        <div style={{ width: '38%', textAlign: 'left' }}>Ingredient</div>
                                                        <div style={{ width: '40%', textAlign: 'center' }}>Required Quantity</div>
                                                        <div style={{ width: '40%', textAlign: 'center' }}>I Have</div>
                                                    </div>
                                                    
                                                    {neededThisWeek.map(item => renderIngredientItem(item, '#52cd92', true))}
                                                </div>
                                            )}

                                            {/* Section 2: Not Used This Week */}
                                            {notUsedThisWeek.length > 0 && (
                                                <div className="mb-5">
                                                    <h3 style={{ 
                                                        fontWeight: '700', 
                                                        marginTop: '1.5rem', 
                                                        marginBottom: '1rem',
                                                        textAlign: 'center',
                                                        color: '#999',
                                                        fontSize: '1.5rem'
                                                    }}>
                                                        Other Items in Pantry
                                                    </h3>
                                                    
                                                    {/* Column Headers for this section */}
                                                    <div className="d-flex justify-content-between align-items-center px-3 py-2 mb-2" 
                                                         style={{ 
                                                             fontWeight: '600', 
                                                             borderBottom: '2px solid #999',
                                                             fontSize: '0.9rem',
                                                             color: '#999'
                                                         }}>
                                                        <div style={{ width: '30%', textAlign: 'left' }}>Ingredient</div>
                                                        <div style={{ width: '40%', textAlign: 'center' }}>Quantity</div>
                                                        <div style={{ width: '30%', textAlign: 'center' }}></div>
                                                    </div>
                                                    
                                                    {Object.keys(notUsedGrouped).sort().map(foodGroup => (
                                                        <div key={foodGroup} className="mb-3">
                                                            <h4 style={{ 
                                                                fontWeight: '700', 
                                                                marginTop: '1rem', 
                                                                marginBottom: '0.5rem',
                                                                textAlign: 'left',
                                                                paddingLeft: '10px',
                                                                fontSize: '1.1rem',
                                                                color: '#999'
                                                            }}>
                                                                {foodGroup}
                                                            </h4>
                                                            {notUsedGrouped[foodGroup].map(item => renderIngredientItem(item, 'transparent', false))}
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </>
                                    )}
                                </div>
                            </div>
                        )}
                    </>
                )}

                {/* Sticky Grocery List Button at Bottom */}
                {currentGroceryList && (
                    <div 
                        className="sticky-grocery-button"
                        style={{
                            position: 'fixed',
                            left: '0',
                            right: '0',
                            zIndex: 1001,
                            backgroundColor: 'white',
                            borderTop: '1px solid #e0e0e0',
                            padding: '12px 16px',
                            boxShadow: '0 -2px 10px rgba(0,0,0,0.1)'
                        }}
                    >
                        <Button
                            onClick={() => setIsDrawerOpen(true)}
                            disabled={!currentGroceryList}
                            style={{
                                backgroundColor: '#52cd92',
                                border: 'none',
                                color: 'white',
                                padding: '0.75rem 1.5rem',
                                fontSize: '1rem',
                                fontWeight: '600',
                                width: '100%',
                                maxWidth: '400px',
                                margin: '0 auto',
                                display: 'block',
                                borderRadius: '8px',
                                boxShadow: '0 2px 8px rgba(82, 205, 146, 0.3)'
                            }}
                        >
                            View Grocery List
                        </Button>
                    </div>
                )}

                {/* Toast Notification */}
                <Toast
                    show={showToast}
                    onClose={() => setShowToast(false)}
                    style={{
                        position: 'fixed',
                        top: '20px',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        zIndex: 9999,
                        backgroundColor: toastType === 'success' ? '#52cd92' : '#dc3545',
                        border: 'none',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                        minWidth: '300px'
                    }}
                >
                    <Toast.Header
                        style={{
                            backgroundColor: toastType === 'success' ? '#52cd92' : '#dc3545',
                            borderBottom: '1px solid rgba(255,255,255,0.2)',
                            justifyContent: 'center'
                        }}
                    >
                        <strong style={{ color: 'white' }}>{toastMessage}</strong>
                    </Toast.Header>
                </Toast>

                {/* Grocery Drawer */}
                <GroceryDrawer
                    key={groceryDrawerRefreshKey}
                    isOpen={isDrawerOpen}
                    onClose={() => setIsDrawerOpen(false)}
                    groceryListId={currentGroceryList?.grocery_list?.id}
                    onItemsAddedToPantry={handleItemsAddedToPantry}
                />

                {/* Recipes For Ingredient Drawer */}
                <RecipesForIngredientDrawer
                    isOpen={isRecipeDrawerOpen}
                    onClose={() => setIsRecipeDrawerOpen(false)}
                    ingredient={selectedIngredientForRecipes}
                />
            </div>
        </ErrorBoundary>
    );
};

export default PantryAndGroceries;

